/**
 * The three MPR panes (VolumetricOrthoView + OrthoPanelView): an orthonormal triad seeded on
 * the centerline tangent frame at the VBR plane, one slice quad and one orthographic camera per
 * pane, shared window/level, slice offsets along the normals (1 slider unit = 1 mm), zoom, pan,
 * crosshair drag/rotate, projected measurement overlays drawn on a 2D canvas per pane.
 */
import * as THREE from 'three';
import { LAYER_SLICE_BASE, type Viewer, type ViewRect } from '@/theater/Viewer';
import { MM_PER_UNIT, type VolumeGrid, volumeExtentMm } from '@/theater/frame';
import type { VolumeObject } from '@/volume/VolumeObject';
import { sliceFragmentShader, sliceVertexShader } from './sliceShader';
import { huToNormalized } from '@/volume/tf';
import type { Rgba } from '@/inventory/colors';
import { DISABLED_CUSP_PLANE, MPR_LEAFLET_TAP_MM } from '@/volume/leaflets';

export type PaneId = 'x' | 'y' | 'z'; // internal names; UI labels: y -> "X" (red), x -> "Y" (green), z -> "Z" (blue)

export const PANE_LABEL: Record<PaneId, string> = { y: 'X', x: 'Y', z: 'Z' };
export const PANE_COLOR: Record<PaneId, string> = { y: 'rgb(220,60,60)', x: 'rgb(60,180,60)', z: 'rgb(60,120,220)' };
/** Horizontal partner per pane; the vertical partner is the remaining one. */
const HORIZONTAL_PARTNER: Record<PaneId, PaneId> = { x: 'y', y: 'z', z: 'y' };
const VERTICAL_PARTNER: Record<PaneId, PaneId> = { x: 'z', y: 'x', z: 'x' };
const LAYER_OF: Record<PaneId, number> = { x: LAYER_SLICE_BASE, y: LAYER_SLICE_BASE + 1, z: LAYER_SLICE_BASE + 2 };

const DEFAULT_WINDOW_LEVEL_HU = 1030;
const DEFAULT_WINDOW_WIDTH_HU = 1900;
const MIN_WINDOW_WIDTH = 0.001;
const ZOOM_SCALE_MIN = 0.05;
const ZOOM_SCALE_MAX = 3;

export interface OverlayCurve {
  key: string;
  points: THREE.Vector3[]; // scene mm
  closed: boolean;
  color: Rgba;
  fade: boolean;
  cull: boolean; // STJ-style near-plane cull (VBR: no cull, fade only)
}
export interface OverlayDot {
  key: string;
  point: THREE.Vector3;
  color: Rgba;
}

export interface Pane {
  id: PaneId;
  element: HTMLElement | null;
  overlay: HTMLCanvasElement | null;
  plane: THREE.Mesh;
  camera: THREE.OrthographicCamera;
  normal: THREE.Vector3; // world (Unity units) unit normal
  basePosition: THREE.Vector3; // world, before the slice offset
  forward: THREE.Vector3; // in-plane "up" direction (world)
  offset: number; // slider units (mm)
  zoom: number; // 0..1 slider, 0.5 = fit
  pan: THREE.Vector2; // world units along camera right/up
  mirrored: boolean; // the XZ ("X") pane's camera sits on the opposite side
  uiAngle: number;
}

export class MprSet {
  readonly panes: Record<PaneId, Pane>;
  readonly material: THREE.ShaderMaterial;
  readonly group = new THREE.Group();
  window: [number, number];
  gain = 0;
  contrast = 1;
  curves: OverlayCurve[] = [];
  dots: OverlayDot[] = [];
  showOverlays = true;
  private viewer: Viewer;
  private volume: VolumeObject;
  private pivotWorld = new THREE.Vector3();
  private userDelta = new THREE.Quaternion();
  private baseRotation = new THREE.Quaternion();
  private locked = false;
  private halfSizeUnits: number;
  private worldPerUnit = 0.01; // SLICE_OFFSET_SENSITIVITY: 0.01 world units per slider unit

  constructor(viewer: Viewer, volume: VolumeObject, grid: VolumeGrid, huRange: [number, number] = [-1024, 3071]) {
    this.viewer = viewer;
    this.volume = volume;
    this.group.name = 'mpr';
    const half = DEFAULT_WINDOW_WIDTH_HU / 2;
    this.window = [huToNormalized(DEFAULT_WINDOW_LEVEL_HU - half, huRange[0], huRange[1]), huToNormalized(DEFAULT_WINDOW_LEVEL_HU + half, huRange[0], huRange[1])];
    const extent = volumeExtentMm(grid);
    this.halfSizeUnits = (Math.max(extent.x, extent.y, extent.z) / 2) / MM_PER_UNIT;
    const diag = extent.length();
    this.material = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: sliceVertexShader,
      fragmentShader: sliceFragmentShader,
      uniforms: {
        uData: { value: volume.material.uniforms.uData.value },
        uVolumeInverse: { value: new THREE.Matrix4() },
        uMinVal: { value: this.window[0] },
        uMaxVal: { value: this.window[1] },
        uGain: { value: 0 },
        uContrast: { value: 1 },
        uAlpha: { value: 1 },
        uUseFull: { value: 0 },
        uFullData: { value: volume.material.uniforms.uData.value },
        uFullUvwMin: { value: new THREE.Vector3() },
        uFullUvwScale: { value: new THREE.Vector3(1, 1, 1) },
        uUseLabels: { value: 0 },
        uLabels: { value: volume.material.uniforms.uData.value },
        uLabelLut: { value: makeLabelLut(new Map()) },
        uLeafletEnabled: { value: 0 },
        uCusp: { value: volume.material.uniforms.uData.value },
        uCuspDistMaxMm: { value: 4 },
        uCuspT0: { value: 0.5 },
        uCuspT1: { value: 1.2 },
        uCuspPlane: { value: new THREE.Vector4(0, 0, 0, 1) },
        uLeafletFill: { value: 1 },
        // through-plane taps of the leaflet overlay: OrthoTapMm converted to uvw per axis
        uStepUvw: { value: new THREE.Vector3(MPR_LEAFLET_TAP_MM / extent.x, MPR_LEAFLET_TAP_MM / extent.y, MPR_LEAFLET_TAP_MM / extent.z) },
      },
      side: THREE.DoubleSide,
      depthWrite: true,
      depthTest: true,
      transparent: false,
    });
    const make = (id: PaneId, mirrored: boolean): Pane => {
      const sizeUnits = (diag * 1.2) / MM_PER_UNIT;
      const plane = new THREE.Mesh(new THREE.PlaneGeometry(sizeUnits, sizeUnits), this.material);
      plane.layers.set(LAYER_OF[id]);
      plane.renderOrder = -1;
      plane.name = `slice-${id}`;
      this.group.add(plane);
      const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.001, 100);
      camera.layers.set(LAYER_OF[id]);
      return {
        id,
        element: null,
        overlay: null,
        plane,
        camera,
        normal: new THREE.Vector3(0, 1, 0),
        basePosition: new THREE.Vector3(),
        forward: new THREE.Vector3(0, 0, 1),
        offset: 0,
        zoom: 0.5,
        pan: new THREE.Vector2(),
        mirrored,
        uiAngle: 0,
      };
    };
    this.panes = { x: make('x', false), y: make('y', true), z: make('z', false) };
  }

  // ------------------------------------------------------------ triad
  /**
   * MoveSlicesAlongCenterline(0) + ApplyCuttingTriadFromBases. Inputs in scene mm. The "X" pane
   * (internal y) is the cross-section: its normal is the VBR plane normal flipped to agree with
   * the centerline tangent; the other two carry the tangent frame.
   */
  seedTriad(centerMm: THREE.Vector3, tangentMm: THREE.Vector3, vbrNormalMm: THREE.Vector3 | null, vbrCenterMm: THREE.Vector3 | null): void {
    if (this.locked) {
      this.applyOffsets();
      return;
    }
    const toWorld = (v: THREE.Vector3) => v.clone().applyMatrix4(this.viewer.root.matrixWorld);
    const toWorldDir = (v: THREE.Vector3) => v.clone().transformDirection(this.viewer.root.matrixWorld).normalize();
    this.viewer.root.updateMatrixWorld(true);
    let center = toWorld(centerMm);
    const t = toWorldDir(tangentMm);
    const worldUp = toWorldDir(new THREE.Vector3(0, 1, 0));
    // baseY = LookRotation(t, up) * Euler(90,0,0): plane normal (local +Y) = t, local forward = -up⊥
    const lookT = lookRotation(t, worldUp);
    let baseY = lookT.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(THREE.MathUtils.degToRad(90), 0, 0)));
    if (vbrNormalMm && vbrCenterMm) {
      let n = toWorldDir(vbrNormalMm);
      const yUp = new THREE.Vector3(0, 1, 0).applyQuaternion(baseY);
      if (n.dot(yUp) < 0) n = n.negate();
      let fwd = new THREE.Vector3(0, 0, 1).applyQuaternion(baseY).projectOnPlane(n);
      if (fwd.lengthSq() < 1e-6) fwd = t.clone().projectOnPlane(n);
      if (fwd.lengthSq() < 1e-6) fwd = worldUp.clone().projectOnPlane(n);
      if (fwd.lengthSq() > 1e-6) baseY = lookRotation(fwd.normalize(), n);
      const vbrC = toWorld(vbrCenterMm);
      const d = center.clone().sub(vbrC).dot(n);
      center = center.sub(n.clone().multiplyScalar(d));
    }
    this.baseRotation.copy(baseY);
    this.pivotWorld.copy(center);
    this.userDelta.identity();
    this.applyTriad();
  }

  private applyTriad(): void {
    const yCut = this.baseRotation.clone().multiply(this.userDelta);
    const xCut = yCut.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(THREE.MathUtils.degToRad(-90), 0, 0)));
    const zCut = xCut.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, THREE.MathUtils.degToRad(90))));
    const up = (q: THREE.Quaternion) => new THREE.Vector3(0, 1, 0).applyQuaternion(q).normalize();
    const fwd = (q: THREE.Quaternion) => new THREE.Vector3(0, 0, 1).applyQuaternion(q).normalize();
    const set = (pane: Pane, q: THREE.Quaternion) => {
      pane.normal.copy(up(q));
      pane.forward.copy(fwd(q).projectOnPlane(pane.normal).normalize());
      pane.basePosition.copy(this.pivotWorld);
    };
    set(this.panes.x, xCut);
    set(this.panes.y, yCut);
    set(this.panes.z, zCut);
    this.applyOffsets();
  }

  applyOffsets(): void {
    // The slice quads live in world space (the group is added to the scene, not the flipped
    // root): PlaneGeometry's +Z is the pane normal and +Y the in-plane forward.
    for (const pane of Object.values(this.panes)) {
      const pos = pane.basePosition.clone().add(pane.normal.clone().multiplyScalar(pane.offset * this.worldPerUnit));
      pane.plane.position.copy(pos);
      pane.plane.quaternion.copy(lookRotation(pane.normal, pane.forward));
      pane.plane.updateMatrixWorld(true);
      this.updateCamera(pane);
    }
    this.viewer.invalidate();
  }

  private updateCamera(pane: Pane): void {
    const centre = pane.basePosition.clone().add(pane.normal.clone().multiplyScalar(pane.offset * this.worldPerUnit));
    const dir = pane.mirrored ? pane.normal.clone() : pane.normal.clone().negate(); // camera sits on -normal side (mirrored: +)
    const camPos = centre.clone().sub(dir.clone().multiplyScalar(2));
    pane.camera.position.copy(camPos);
    pane.camera.up.copy(pane.forward);
    pane.camera.lookAt(centre);
    // pan in camera right/up
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(pane.camera.quaternion);
    const upv = new THREE.Vector3(0, 1, 0).applyQuaternion(pane.camera.quaternion);
    pane.camera.position.add(right.multiplyScalar(pane.pan.x)).add(upv.multiplyScalar(pane.pan.y));
    const zoom = zoomScale(pane.zoom);
    const halfH = this.halfSizeUnits * zoom;
    const aspect = pane.element && pane.element.clientHeight > 0 ? pane.element.clientWidth / pane.element.clientHeight : 1;
    pane.camera.left = -halfH * aspect;
    pane.camera.right = halfH * aspect;
    pane.camera.top = halfH;
    pane.camera.bottom = -halfH;
    pane.camera.near = 0.001;
    pane.camera.far = 10;
    pane.camera.updateProjectionMatrix();
    pane.camera.updateMatrixWorld(true);
  }

  // ------------------------------------------------------------ uniforms
  beforeRender(): void {
    const u = this.material.uniforms;
    this.volume.mesh.updateMatrixWorld();
    (u.uVolumeInverse.value as THREE.Matrix4).copy(this.volume.mesh.matrixWorld).invert();
    u.uData.value = this.volume.material.uniforms.uData.value;
    u.uMinVal.value = this.window[0];
    u.uMaxVal.value = this.window[1];
    u.uGain.value = this.gain;
    u.uContrast.value = this.contrast;
  }

  setWindow(min: number, max: number): void {
    const lo = Math.min(1, Math.max(0, Math.min(min, max)));
    const hi = Math.min(1, Math.max(0, Math.max(min, max)));
    this.window = sanitize([lo, hi]);
    this.invalidate();
  }

  /** ApplyOrthoWindowLevelDrag: dy raises the level, dx widens the window. */
  windowLevelDrag(start: [number, number], dxPx: number, dyPx: number): void {
    const s = sanitize(start);
    const startCenter = 0.5 * (s[0] + s[1]);
    const startWidth = Math.max(MIN_WINDOW_WIDTH, s[1] - s[0]);
    const centerShift = -dyPx * 0.0015;
    const widthScale = Math.exp(dxPx * 0.01);
    const newWidth = Math.min(1, Math.max(MIN_WINDOW_WIDTH, startWidth * widthScale));
    const half = newWidth / 2;
    const c = Math.min(1 - half, Math.max(half, startCenter + centerShift));
    this.window = [c - half, c + half];
    this.invalidate();
  }

  setLabels(tex: THREE.Data3DTexture | null, lut: Map<number, Rgba>): void {
    const u = this.material.uniforms;
    u.uUseLabels.value = tex ? 1 : 0;
    u.uLabels.value = tex ?? this.volume.material.uniforms.uData.value;
    (u.uLabelLut.value as THREE.DataTexture).dispose();
    u.uLabelLut.value = makeLabelLut(lut);
    this.invalidate();
  }

  // ------------------------------------------------------------ leaflet overlay
  private leafletOverlayOn = false;
  private cuspBound = false;

  /** The frame's cusp field (null unbinds); the red leaflet-fill overlay draws while the checkbox is on. */
  setCuspField(tex: THREE.Data3DTexture | null, distMaxMm = 4, t0Mm = 0.35, t1Mm = 1): void {
    const u = this.material.uniforms;
    this.cuspBound = !!tex;
    u.uCusp.value = tex ?? this.volume.material.uniforms.uData.value;
    u.uCuspDistMaxMm.value = Math.max(0.001, distMaxMm);
    u.uCuspT0.value = Math.max(0, t0Mm);
    u.uCuspT1.value = Math.max(t0Mm + 0.05, t1Mm);
    this.refreshLeafletEnabled();
  }

  setCuspPlane(plane: THREE.Vector4 | null): void {
    (this.material.uniforms.uCuspPlane.value as THREE.Vector4).copy(plane ?? DISABLED_CUSP_PLANE);
    this.invalidate();
  }

  setLeafletFill(fill: number): void {
    this.material.uniforms.uLeafletFill.value = Math.min(2, Math.max(0, fill));
    this.invalidate();
  }

  /** "Show leaflet fill in orthoviews" (vascularLeafletOrthoToggle). */
  setLeafletOverlay(on: boolean): void {
    this.leafletOverlayOn = on;
    this.refreshLeafletEnabled();
  }

  private refreshLeafletEnabled(): void {
    this.material.uniforms.uLeafletEnabled.value = this.leafletOverlayOn && this.cuspBound ? 1 : 0;
    this.invalidate();
  }

  setFullVolume(tex: THREE.Data3DTexture | null, uvwMin: THREE.Vector3, uvwScale: THREE.Vector3): void {
    const u = this.material.uniforms;
    u.uUseFull.value = tex ? 1 : 0;
    u.uFullData.value = tex ?? this.volume.material.uniforms.uData.value;
    (u.uFullUvwMin.value as THREE.Vector3).copy(uvwMin);
    (u.uFullUvwScale.value as THREE.Vector3).copy(uvwScale);
    this.invalidate();
  }

  // ------------------------------------------------------------ pane control
  bind(id: PaneId, element: HTMLElement, overlay: HTMLCanvasElement): void {
    const pane = this.panes[id];
    pane.element = element;
    pane.overlay = overlay;
    this.viewer.addView({
      id: `mpr-${id}`,
      element,
      camera: pane.camera,
      layer: LAYER_OF[id],
      dirty: true,
      background: new THREE.Color(0x000000),
      before: () => {
        this.updateCamera(pane);
        this.beforeRender();
      },
      after: (rect) => this.drawOverlay(pane, rect),
    });
  }

  unbind(id: PaneId): void {
    this.panes[id].element = null;
    this.panes[id].overlay = null;
    this.viewer.removeView(`mpr-${id}`);
  }

  invalidate(): void {
    for (const id of ['x', 'y', 'z'] as PaneId[]) this.viewer.invalidate(`mpr-${id}`);
  }

  setOffset(id: PaneId, value: number): void {
    this.panes[id].offset = value;
    this.applyOffsets();
  }

  setZoom(id: PaneId | 'all', value: number): void {
    const v = Math.min(1, Math.max(0, value));
    for (const pane of Object.values(this.panes)) if (id === 'all' || pane.id === id) pane.zoom = v;
    this.applyOffsets();
  }

  panByViewportDelta(id: PaneId, dx: number, dy: number): void {
    const pane = this.panes[id];
    const halfH = Math.max(1e-4, pane.camera.top);
    const halfW = halfH * Math.max(1e-4, (pane.camera.right - pane.camera.left) / (pane.camera.top - pane.camera.bottom));
    pane.pan.x += -dx * (2 * halfW);
    pane.pan.y += -dy * (2 * halfH);
    this.applyOffsets();
  }

  resetOffsets(): void {
    for (const pane of Object.values(this.panes)) {
      pane.offset = 0;
      pane.pan.set(0, 0);
    }
    this.applyOffsets();
  }

  /** Centre of a pane's current slice plane (world units). */
  sliceCentre(id: PaneId): THREE.Vector3 {
    const pane = this.panes[id];
    return pane.basePosition.clone().add(pane.normal.clone().multiplyScalar(pane.offset * this.worldPerUnit));
  }

  /** Pane pixel -> world point on the pane's plane. */
  pixelToWorld(id: PaneId, px: number, py: number): THREE.Vector3 | null {
    const pane = this.panes[id];
    const el = pane.element;
    if (!el) return null;
    const vx = (px / el.clientWidth) * 2 - 1;
    const vy = -((py / el.clientHeight) * 2 - 1);
    const p = new THREE.Vector3(vx, vy, 0).unproject(pane.camera);
    const centre = pane.basePosition.clone().add(pane.normal.clone().multiplyScalar(pane.offset * this.worldPerUnit));
    const drift = p.clone().sub(centre).dot(pane.normal);
    return p.sub(pane.normal.clone().multiplyScalar(drift));
  }

  worldToPixel(id: PaneId, world: THREE.Vector3): { x: number; y: number; depth: number } | null {
    const pane = this.panes[id];
    const el = pane.element;
    if (!el) return null;
    const v = world.clone().project(pane.camera);
    return { x: ((v.x + 1) / 2) * el.clientWidth, y: ((1 - v.y) / 2) * el.clientHeight, depth: v.z };
  }

  /** ApplySliceOffsetFromWorldPoint for the given axes. */
  moveSlicesTo(axes: PaneId[], world: THREE.Vector3): void {
    for (const id of axes) {
      const pane = this.panes[id];
      pane.offset = world.clone().sub(pane.basePosition).dot(pane.normal) / this.worldPerUnit;
    }
    this.locked = true;
    this.applyOffsets();
  }

  /** Crosshair rotate about the pane's normal (roll), pivot = triad intersection. */
  rotateTriad(id: PaneId, uiDeltaDeg: number): void {
    const pane = this.panes[id];
    const pivot = this.intersection();
    const sign = pane.normal.dot(new THREE.Vector3(0, 0, -1).applyQuaternion(pane.camera.quaternion)) < 0 ? -1 : 1;
    const worldDelta = new THREE.Quaternion().setFromAxisAngle(pane.normal, THREE.MathUtils.degToRad(-uiDeltaDeg * sign));
    // userLocalDelta = inv(baseY) * worldDelta * baseY * startDelta
    const invBase = this.baseRotation.clone().invert();
    this.userDelta.copy(invBase.multiply(worldDelta).multiply(this.baseRotation).multiply(this.userDelta)).normalize();
    this.locked = true;
    // Preserve the pivot: offsets are kept, base position moves so the intersection stays put.
    const yCut = this.baseRotation.clone().multiply(this.userDelta);
    const xCut = yCut.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(THREE.MathUtils.degToRad(-90), 0, 0)));
    const zCut = xCut.clone().multiply(new THREE.Quaternion().setFromEuler(new THREE.Euler(0, 0, THREE.MathUtils.degToRad(90))));
    const nx = new THREE.Vector3(0, 1, 0).applyQuaternion(xCut).normalize();
    const ny = new THREE.Vector3(0, 1, 0).applyQuaternion(yCut).normalize();
    const nz = new THREE.Vector3(0, 1, 0).applyQuaternion(zCut).normalize();
    const offsetSum = nx.clone().multiplyScalar(this.panes.x.offset).add(ny.clone().multiplyScalar(this.panes.y.offset)).add(nz.clone().multiplyScalar(this.panes.z.offset));
    this.pivotWorld.copy(pivot).sub(offsetSum.multiplyScalar(this.worldPerUnit));
    this.applyTriad();
  }

  /** The point where all three planes meet. */
  intersection(): THREE.Vector3 {
    const out = new THREE.Vector3();
    for (const pane of Object.values(this.panes)) {
      const pos = pane.basePosition.clone().add(pane.normal.clone().multiplyScalar(pane.offset * this.worldPerUnit));
      out.add(pane.normal.clone().multiplyScalar(pos.dot(pane.normal)));
    }
    return out;
  }

  // ------------------------------------------------------------ overlays (2D canvas)
  armGeometry(pane: Pane, partner: Pane): { cx: number; cy: number; angleDeg: number } | null {
    const el = pane.element;
    if (!el) return null;
    const viewN = pane.normal;
    const partnerN = partner.normal;
    const inPlane = partnerN.clone().sub(viewN.clone().multiplyScalar(partnerN.dot(viewN)));
    const denom = inPlane.lengthSq();
    if (denom < 1e-9) return null;
    const viewPos = pane.basePosition.clone().add(viewN.clone().multiplyScalar(pane.offset * this.worldPerUnit));
    const partnerPos = partner.basePosition.clone().add(partnerN.clone().multiplyScalar(partner.offset * this.worldPerUnit));
    const t = partnerPos.clone().sub(viewPos).dot(partnerN) / denom;
    const world = viewPos.clone().add(inPlane.multiplyScalar(t));
    const px = this.worldToPixel(pane.id, world);
    if (!px) return null;
    const lineDir = new THREE.Vector3().crossVectors(viewN, partnerN).normalize();
    const right = new THREE.Vector3(1, 0, 0).applyQuaternion(pane.camera.quaternion);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(pane.camera.quaternion);
    const angleDeg = THREE.MathUtils.radToDeg(Math.atan2(-lineDir.dot(up), lineDir.dot(right)));
    return { cx: px.x, cy: px.y, angleDeg };
  }

  private drawOverlay(pane: Pane, rect: ViewRect): void {
    const canvas = pane.overlay;
    const el = pane.element;
    if (!canvas || !el) return;
    const dpr = window.devicePixelRatio;
    const w = Math.round(rect.width);
    const h = Math.round(rect.height);
    if (canvas.width !== w * dpr || canvas.height !== h * dpr) {
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      canvas.style.width = `${w}px`;
      canvas.style.height = `${h}px`;
    }
    const ctx = canvas.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, w, h);
    // slice tolerance: world units; quarter of the smallest half-extent of an oblique quad is
    // tiny for axis-aligned planes, so use an explicit 1 mm tolerance (0.01 units).
    const sliceTol = 0.01;
    const centre = pane.basePosition.clone().add(pane.normal.clone().multiplyScalar(pane.offset * this.worldPerUnit));
    const planeDist = (world: THREE.Vector3) => world.clone().sub(centre).dot(pane.normal);
    if (this.showOverlays) {
      const toWorld = (v: THREE.Vector3) => v.clone().applyMatrix4(this.viewer.root.matrixWorld);
      ctx.lineWidth = 4;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      for (const curve of this.curves) {
        const pts = curve.points.map((p) => {
          const world = toWorld(p);
          const d = planeDist(world);
          const px = this.worldToPixel(pane.id, world);
          if (!px) return null;
          const depth = Math.min(1, Math.abs(d) / sliceTol);
          const inside = px.x >= 0 && px.x <= w && px.y >= 0 && px.y <= h;
          if (curve.cull && (Math.abs(d) > sliceTol || !inside)) return null;
          return { ...px, depth };
        });
        const list = curve.closed && pts.length ? [...pts, pts[0]] : pts;
        for (let i = 0; i + 1 < list.length; i++) {
          const a = list[i];
          const b = list[i + 1];
          if (!a || !b) continue;
          const depthFactor = Math.max(a.depth, b.depth);
          const shade = 1 - 0.65 * depthFactor;
          let alpha = curve.color[3];
          if (curve.fade) alpha *= 1 - depthFactor;
          if (alpha <= 0.01) continue;
          ctx.strokeStyle = `rgba(${curve.color[0] * 255 * shade}, ${curve.color[1] * 255 * shade}, ${curve.color[2] * 255 * shade}, ${alpha})`;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }
      }
      for (const dot of this.dots) {
        const world = toWorld(dot.point);
        const d = planeDist(world);
        if (Math.abs(d) > sliceTol) continue;
        const px = this.worldToPixel(pane.id, world);
        if (!px || px.x < 0 || px.x > w || px.y < 0 || px.y > h) continue;
        const depth = Math.min(1, Math.abs(d) / sliceTol);
        const shade = 1 - 0.65 * depth;
        const alpha = dot.color[3] * (1 - depth);
        if (alpha <= 0.01) continue;
        ctx.fillStyle = `rgba(${dot.color[0] * 255 * shade}, ${dot.color[1] * 255 * shade}, ${dot.color[2] * 255 * shade}, ${alpha})`;
        ctx.strokeStyle = `rgba(0,0,0,${alpha * 0.35})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(px.x, px.y, 7, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
        ctx.lineWidth = 4;
      }
    }
    // crosshair arms: horizontal partner angle, vertical = +90
    const hp = this.panes[HORIZONTAL_PARTNER[pane.id]];
    const vp = this.panes[VERTICAL_PARTNER[pane.id]];
    const arm = (partner: Pane, color: string, angleOverride?: number) => {
      const g = this.armGeometry(pane, partner);
      if (!g) return;
      const inside = (g.cx >= 0 && g.cx <= w) || (g.cy >= 0 && g.cy <= h) || (g.cx >= -w * 0.25 && g.cx <= w * 1.25 && g.cy >= -h * 0.25 && g.cy <= h * 1.25);
      if (!inside) return;
      const len = Math.hypot(w, h);
      const a = THREE.MathUtils.degToRad(angleOverride ?? g.angleDeg);
      ctx.save();
      ctx.globalAlpha = 0.8;
      ctx.strokeStyle = color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(g.cx - Math.cos(a) * len, g.cy - Math.sin(a) * len);
      ctx.lineTo(g.cx + Math.cos(a) * len, g.cy + Math.sin(a) * len);
      ctx.stroke();
      ctx.restore();
    };
    const hg = this.armGeometry(pane, hp);
    pane.uiAngle = hg?.angleDeg ?? 0;
    arm(hp, PANE_COLOR[hp.id]);
    arm(vp, PANE_COLOR[vp.id], pane.uiAngle + 90);
  }

  /** Hit test for the crosshair: 'both' near the intersection, otherwise which partner arm. */
  hitCrosshair(id: PaneId, px: number, py: number): { mode: 'both' | 'drag' | 'rotate'; axis: PaneId | PaneId[] } | null {
    const pane = this.panes[id];
    const el = pane.element;
    if (!el) return null;
    const hp = this.panes[HORIZONTAL_PARTNER[id]];
    const vp = this.panes[VERTICAL_PARTNER[id]];
    const g = this.armGeometry(pane, hp);
    if (!g) return null;
    const ix = g.cx;
    const iy = g.cy;
    const dist = Math.hypot(px - ix, py - iy);
    if (dist <= 14) return { mode: 'both', axis: [hp.id, vp.id] };
    const a = THREE.MathUtils.degToRad(g.angleDeg);
    const distToLine = (angle: number) => Math.abs(-(px - ix) * Math.sin(angle) + (py - iy) * Math.cos(angle));
    const onH = distToLine(a) <= 10;
    const onV = distToLine(a + Math.PI / 2) <= 10;
    if (onH && onV) return { mode: 'both', axis: [hp.id, vp.id] };
    if (!onH && !onV) return null;
    const halfMin = 0.5 * Math.min(el.clientWidth, el.clientHeight);
    if (dist / halfMin >= 0.55) return { mode: 'rotate', axis: id };
    return { mode: 'drag', axis: onH ? hp.id : vp.id };
  }

  crosshairCenterPx(id: PaneId): { x: number; y: number } | null {
    const pane = this.panes[id];
    const g = this.armGeometry(pane, this.panes[HORIZONTAL_PARTNER[id]]);
    return g ? { x: g.cx, y: g.cy } : null;
  }

  setPlaneVisibleIn3D(id: PaneId, on: boolean): void {
    const pane = this.panes[id];
    if (on) pane.plane.layers.enable(0);
    else pane.plane.layers.disable(0);
    this.viewer.invalidate('main');
  }

  dispose(): void {
    for (const id of ['x', 'y', 'z'] as PaneId[]) this.unbind(id);
    this.material.dispose();
    for (const pane of Object.values(this.panes)) pane.plane.geometry.dispose();
  }
}

/** Zoom slider 0..1 -> scale (3 .. 1 .. 0.05), 0.5 = fit. */
export function zoomScale(slider: number): number {
  const t = Math.min(1, Math.max(0, slider));
  if (t <= 0.5) return THREE.MathUtils.lerp(ZOOM_SCALE_MAX, 1, t / 0.5);
  return THREE.MathUtils.lerp(1, ZOOM_SCALE_MIN, (t - 0.5) / 0.5);
}

function sanitize(w: [number, number]): [number, number] {
  let lo = Math.min(1, Math.max(0, Math.min(w[0], w[1])));
  let hi = Math.min(1, Math.max(0, Math.max(w[0], w[1])));
  if (hi - lo < MIN_WINDOW_WIDTH) {
    const c = 0.5 * (lo + hi);
    lo = Math.min(1, Math.max(0, c - MIN_WINDOW_WIDTH / 2));
    hi = Math.min(1, Math.max(0, c + MIN_WINDOW_WIDTH / 2));
    if (hi - lo < MIN_WINDOW_WIDTH) {
      lo = Math.min(1, Math.max(0, hi - MIN_WINDOW_WIDTH));
      hi = Math.min(1, Math.max(0, lo + MIN_WINDOW_WIDTH));
    }
  }
  return [lo, hi];
}

/** Unity LookRotation(forward, up) as a three.js quaternion with local +Z = forward, +Y = up. */
export function lookRotation(forward: THREE.Vector3, up: THREE.Vector3): THREE.Quaternion {
  const f = forward.clone().normalize();
  let u = up.clone().projectOnPlane(f);
  if (u.lengthSq() < 1e-8) u = Math.abs(f.y) < 0.9 ? new THREE.Vector3(0, 1, 0).projectOnPlane(f) : new THREE.Vector3(1, 0, 0).projectOnPlane(f);
  u.normalize();
  const r = new THREE.Vector3().crossVectors(u, f).normalize();
  const m = new THREE.Matrix4().makeBasis(r, u, f);
  return new THREE.Quaternion().setFromRotationMatrix(m);
}

function makeLabelLut(lut: Map<number, Rgba>): THREE.DataTexture {
  const data = new Uint8Array(256 * 4);
  for (const [id, c] of lut) {
    if (id < 0 || id > 255) continue;
    data[id * 4] = Math.round(c[0] * 255);
    data[id * 4 + 1] = Math.round(c[1] * 255);
    data[id * 4 + 2] = Math.round(c[2] * 255);
    data[id * 4 + 3] = Math.round(c[3] * 255);
  }
  const tex = new THREE.DataTexture(data, 256, 1, THREE.RGBAFormat, THREE.UnsignedByteType);
  tex.minFilter = THREE.NearestFilter;
  tex.magFilter = THREE.NearestFilter;
  tex.needsUpdate = true;
  return tex;
}
